import Site from '@/common/models/site/Site';
import ErrorHandler from '@/ErrorHandler';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import API from '@/org-app/api';
import { createPatchAction } from '@/utils/ActionsUtils';
import i18n from '@/vueI18n';

import * as types from './mutation-types';

const actions = {
  patchSites: createPatchAction(API.instance.patchSites),

  createNewSite({ commit }, site = new Site(-1, '')) {
    commit(types.CREATE_SITE, site);
  },

  deleteSites({ commit }, sites) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      let successfullApiCalls = 0;
      let failedApiCalls = 0;

      const evalCompletion = (error) => {
        if (successfullApiCalls + failedApiCalls === sites.length) {
          if (successfullApiCalls === sites.length) {
            resolve(true);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          } else {
            reject(error);
            const msg = i18n.global.t('messages.failedToDeleteSites');
            ErrorHandler.showError(msg, error);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        }
      };

      sites.forEach((site) => {
        API.instance.deleteSite(
          site.id,
          () => {
            commit(types.DELETE_SITE, site);
            successfullApiCalls += 1;
            evalCompletion();
          },
          (error) => {
            failedApiCalls += 1;
            evalCompletion(error);
          },
        );
      });
    });
  },

  deleteSite({ commit }, site) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      API.instance.deleteSite(
        site.id,
        () => {
          commit(types.DELETE_SITE, site);
          resolve(true);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToDeleteSite');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  getSites({ commit }, params) {
    return new Promise((resolve, reject) => {
      if (params.showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loading);
      }
      API.instance.getSites(
        params,
        (sites) => {
          commit(types.UPDATE_SITES_LIST, sites.items);
          resolve(sites);
          if (params.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetSites');
          ErrorHandler.showError(msg, error);
          reject(error);
          if (params.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
      );
    });
  },

  async getSitesMinimal({ commit }, queryParams) {
    GlobalEventBus.$emit(GlobalEvents.loading);
    const result = await API.instance.getSitesMinimal(queryParams).catch(({ error }) => {
      const msg = i18n.global.t('messages.failedToGetSites');
      ErrorHandler.showError(msg, error);
      GlobalEventBus.$emit(GlobalEvents.loaded);
      throw error;
    });
    GlobalEventBus.$emit(GlobalEvents.loaded);
    return result;
  },

  async getSitesForPublicPlan({ commit }, params) {
    const { cancelTokenSource, showLoader, planUuid, queryParams } = params;
    if (showLoader) GlobalEventBus.$emit(GlobalEvents.loading);

    try {
      return await API.instance.getSitesForPublicPlan({
        params: queryParams.toObject(),
        cancelTokenSource,
        showLoader,
        planUuid,
      });
    } catch (error) {
      if (error != null) {
        const msg = i18n.global.t('messages.failedToGetSites');
        ErrorHandler.showError(msg, error);
        throw error;
      }
    } finally {
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
    }
  },

  getSite({ commit }, { id, expandUnits = true }) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);
      API.instance.getSite(
        { id, expandUnits },
        (site) => {
          commit(types.UPDATE_SITE, site);
          resolve(site);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetSite');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  async getSitesBriefUsingPost({ commit }, { siteIDs, unitIDs = null, networkIDs = null, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const result = await API.instance.getSitesBriefUsingPost(siteIDs, unitIDs, networkIDs).catch(({ error }) => {
      const msg = i18n.global.t('messages.failedToGetSites');
      ErrorHandler.showError(msg, error);
      GlobalEventBus.$emit(GlobalEvents.loaded);
      throw error;
    });
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
    return result;
  },

  saveSite({ commit }, { site, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    return new Promise((resolve, reject) => {
      const successCallback = (data) => {
        commit(types.SAVE_SITE, data);
        resolve(data);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
      };

      const errorCallback = (error) => {
        const msg = i18n.global.t('messages.failedToSaveSite');
        ErrorHandler.showError(msg, error);
        reject(error);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
      };

      if (site.id === -1) {
        API.instance.postSite(site, successCallback, errorCallback);
      } else {
        API.instance.putSite(site, successCallback, errorCallback);
      }
    });
  },
  async getSiteAudit({ commit }, { id, cancelTokenSource, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }

    const result = await API.instance.getSiteAudit({ id, cancelTokenSource }).catch(({ error }) => {
      const msg = i18n.global.t('messages.failedToGetSiteAudit');
      ErrorHandler.showError(msg, error);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw error;
    });
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
    return result;
  },
};

export default actions;
